import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { usePermissions } from "@/hooks/usePermissions";
import {
  PRIORITIES,
  PRIORITY_LABELS,
  ORIGINS,
  OCC_CLASS_LABELS,
} from "@/lib/labels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const NONE = "__none__";

function normalizeTaxonomy(raw) {
  /*
   * O ERP /taxonomy/full pode retornar diretamente:
   *
   *   [...]
   *
   * ou envelopes legados:
   *
   *   { categories: [...] }
   *   { items: [...] }
   *   { taxonomy: { categories: [...] } }
   *   { data: { categories: [...] } }
   */

  if (Array.isArray(raw)) {
    return raw.filter(
      (item) =>
        !!item &&
        typeof item === "object",
    );
  }

  if (!raw || typeof raw !== "object") {
    return [];
  }

  const root = raw;

  if (Array.isArray(root.categories)) {
    return root.categories.filter(
      (item) =>
        !!item &&
        typeof item === "object",
    );
  }

  if (Array.isArray(root.items)) {
    return root.items.filter(
      (item) =>
        !!item &&
        typeof item === "object",
    );
  }

  if (
    root.taxonomy &&
    typeof root.taxonomy === "object"
  ) {
    return normalizeTaxonomy(
      root.taxonomy,
    );
  }

  if (
    root.data &&
    typeof root.data === "object"
  ) {
    return normalizeTaxonomy(
      root.data,
    );
  }

  return [];
}

function normalizeClasses(
  value,
) {
  if (Array.isArray(value)) {
    return value
      .map((item) =>
        String(item || "")
          .trim()
          .toLowerCase(),
      )
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) =>
        item
          .trim()
          .toLowerCase(),
      )
      .filter(Boolean);
  }

  return [];
}

function categorySupportsClass(
  category,
  occurrenceClass,
) {
  const classes = normalizeClasses(
    category.classes,
  );

  /*
   * Categorias antigas sem "classes" permanecem compatíveis
   * com o aplicativo cliente.
   */
  if (!classes.length) {
    return occurrenceClass === "cliente";
  }

  return classes.includes(
    occurrenceClass
      .trim()
      .toLowerCase(),
  );
}

function normalizeCategoryName(
  value,
) {
  return String(
    value ?? "",
  ).trim();
}

export default function OccurrenceEditDialog({
  open,
  onOpenChange,
  occurrence,
  onSaved,
}) {
  const { can, user } = usePermissions();

  const [tree, setTree] = useState([]);

  const [taxonomy, setTaxonomy] = useState([]);

  const [responsibles, setResponsibles] =
    useState([]);

  const [rodentSpecies, setRodentSpecies] =
    useState([]);

  const [rodentSpeciesLoading, setRodentSpeciesLoading] =
    useState(false);

  const [rodentSpeciesError, setRodentSpeciesError] =
    useState(false);

  const [form, setForm] =
    useState(null);

  const [busy, setBusy] =
    useState(false);

  const set = (
    patch,
  ) =>
    setForm(
      (current) => ({
        ...(current || {}),
        ...patch,
      }),
    );

  useEffect(() => {
    if (!open || !occurrence) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      const [
        hierarchyResult,
        taxonomyResult,
        responsiblesResult,
        speciesResult,
      ] = await Promise.allSettled([
        api.get("/hierarchy/tree"),
        api.get("/taxonomy/full"),
        api.get("/responsibles"),
      ]);

      if (cancelled) {
        return;
      }

      if (
        hierarchyResult.status ===
        "fulfilled"
      ) {
        const data =
          hierarchyResult.value?.data;

        setTree(
          Array.isArray(data)
            ? data
            : [],
        );
      } else {
        setTree([]);
      }

      if (
        taxonomyResult.status ===
        "fulfilled"
      ) {
        setTaxonomy(
          normalizeTaxonomy(
            taxonomyResult.value?.data,
          ),
        );
      } else {
        setTaxonomy([]);
      }

      if (
        responsiblesResult.status ===
        "fulfilled"
      ) {
        const data =
          responsiblesResult.value?.data;

        setResponsibles(
          Array.isArray(data)
            ? data
            : Array.isArray(
                  data?.items,
                )
              ? data.items
              : [],
        );
      } else {
        setResponsibles([]);
      }

      // O cadastro de espécies é complementar: qualquer falha aqui
      // jamais pode derrubar o editor inteiro de Ocorrências.
      setRodentSpeciesLoading(true);
      setRodentSpeciesError(false);

      try {
        let response;

        try {
          response = await api.get("/electric-rooms/species");
        } catch (primaryError) {
          response = await api.get("/occurrences/rodent-species");
        }

        if (!cancelled) {
          const data = response?.data;
          const raw = Array.isArray(data)
            ? data
            : Array.isArray(data?.items)
              ? data.items
              : [];

          setRodentSpecies(
            raw
              .filter(
                (item) =>
                  item &&
                  item.active !== false &&
                  (item.id || item.species_id),
              )
              .map((item) => ({
                ...item,
                id: item.id || item.species_id,
                name:
                  item.name ||
                  item.common_name ||
                  item.species_name ||
                  item.scientific_name ||
                  "Espécie",
              })),
          );
        }
      } catch (error) {
        if (!cancelled) {
          console.warn(
            "Cadastro de espécies indisponível:",
            error,
          );
          setRodentSpecies([]);
          setRodentSpeciesError(true);
        }
      } finally {
        if (!cancelled) {
          setRodentSpeciesLoading(false);
        }
      }

      const customFields =
        occurrence?.custom_fields &&
        typeof occurrence.custom_fields === "object"
          ? occurrence.custom_fields
          : {};

      setForm({
        category:
          occurrence.category || "",

        priority:
          occurrence.priority ||
          "media",

        description:
          occurrence.description ||
          "",

        occurred_at: (
          occurrence.occurred_at ||
          ""
        ).slice(
          0,
          16,
        ),

        client_id:
          occurrence.client_id ||
          "",

        unit_id:
          occurrence.unit_id ||
          "",

        area_id:
          occurrence.area_id ||
          "",

        subarea_id:
          occurrence.subarea_id ||
          "",

        type_id:
          occurrence.type_id ||
          "",

        subtype_id:
          occurrence.subtype_id ||
          "",

        origin:
          occurrence.origin ||
          "Inspeção Técnica",

        occurrence_class:
          occurrence.occurrence_class ||
          "cliente",

        responsible_id:
          occurrence.responsible_id ||
          "",

        rodent_species_id:
          occurrence.rodent_species_id ||
          customFields.rodent_species_id ||
          customFields.species_id ||
          "",

        rodent_species_name:
          occurrence.rodent_species_name ||
          customFields.rodent_species_name ||
          customFields.species_name ||
          customFields.especie ||
          "",
      });
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    occurrence,
  ]);

  const client = useMemo(
    () =>
      tree.find(
        (item) =>
          item.id ===
          form?.client_id,
      ),
    [
      tree,
      form?.client_id,
    ],
  );

  const unit = useMemo(
    () =>
      (
        client?.units || []
      ).find(
        (item) =>
          item.id ===
          form?.unit_id,
      ),
    [
      client,
      form?.unit_id,
    ],
  );

  const area = useMemo(
    () =>
      (
        unit?.areas || []
      ).find(
        (item) =>
          item.id ===
          form?.area_id,
      ),
    [
      unit,
      form?.area_id,
    ],
  );

  const occurrenceClass =
    String(
      form?.occurrence_class ||
        "cliente",
    )
      .trim()
      .toLowerCase();

  const visibleCategories =
    useMemo(
      () =>
        taxonomy.filter(
          (category) =>
            categorySupportsClass(
              category,
              occurrenceClass,
            ),
        ),
      [
        taxonomy,
        occurrenceClass,
      ],
    );

  const cat = useMemo(
    () => {
      const categoryName =
        normalizeCategoryName(
          form?.category,
        );

      if (!categoryName) {
        return undefined;
      }

      return visibleCategories.find(
        (category) =>
          normalizeCategoryName(
            category.name,
          ) === categoryName,
      );
    },
    [
      visibleCategories,
      form?.category,
    ],
  );

  const type = useMemo(
    () =>
      (
        cat?.types || []
      ).find(
        (item) =>
          item.id ===
          form?.type_id,
      ),
    [
      cat,
      form?.type_id,
    ],
  );

  const availableTypes =
    useMemo(
      () =>
        Array.isArray(
          cat?.types,
        )
          ? cat.types
          : [],
      [cat],
    );

  const availableSubtypes =
    useMemo(
      () =>
        Array.isArray(
          type?.subtypes,
        )
          ? type.subtypes
          : [],
      [type],
    );

  const selectedTypeName = String(
    type?.name ||
      occurrence?.type_name ||
      "",
  )
    .trim()
    .toLowerCase();

  const isRodentOccurrence =
    Boolean(
      occurrence?.is_rodent ||
      form?.rodent_species_id ||
      form?.rodent_species_name ||
      String(occurrence?.type_name || "")
        .trim()
        .toLowerCase()
        .includes("roedor") ||
      selectedTypeName.includes("roedor") ||
      selectedTypeName.includes("toca") ||
      selectedTypeName.includes("fezes de roedores") ||
      selectedTypeName.includes("trilhas") ||
      selectedTypeName.includes("marcas de roedura"),
    );

  const selectedRodentSpecies =
    useMemo(
      () =>
        rodentSpecies.find(
          (item) =>
            String(item?.id) ===
            String(
              form?.rodent_species_id,
            ),
        ),
      [
        rodentSpecies,
        form?.rodent_species_id,
      ],
    );

  /*
   * Se uma ocorrência antiga possuir uma categoria que deixou de
   * estar disponível para a classe atual, mantemos a categoria
   * existente no Select para que a edição não fique sem valor.
   */
  const categoryItems =
    useMemo(() => {
      const items =
        visibleCategories.map(
          (category) => ({
            id: normalizeCategoryName(
              category.name,
            ),
            name: normalizeCategoryName(
              category.name,
            ),
          }),
        );

      const current =
        normalizeCategoryName(
          form?.category,
        );

      if (
        current &&
        !items.some(
          (item) =>
            item.id === current,
        )
      ) {
        items.unshift({
          id: current,
          name: `${current} (atual)`,
        });
      }

      return items;
    }, [
      visibleCategories,
      form?.category,
    ]);

  if (!form || !occurrence) {
    return null;
  }

  const save = async () => {
    if (
      (
        form.description ||
        ""
      )
        .trim()
        .length < 10
    ) {
      return toast.error(
        "A descrição deve ter no mínimo 10 caracteres",
      );
    }

    if (!form.category) {
      return toast.error(
        "Selecione a categoria da ocorrência",
      );
    }

    if (
      !visibleCategories.some(
        (category) =>
          normalizeCategoryName(
            category.name,
          ) ===
          normalizeCategoryName(
            form.category,
          ),
      )
    ) {
      return toast.error(
        "A categoria selecionada não está disponível para a classificação da ocorrência.",
      );
    }

    if (
      form.type_id &&
      !availableTypes.some(
        (item) =>
          item.id ===
          form.type_id,
      )
    ) {
      return toast.error(
        "O tipo selecionado não pertence à categoria informada.",
      );
    }

    if (
      form.subtype_id &&
      !availableSubtypes.some(
        (item) =>
          item.id ===
          form.subtype_id,
      )
    ) {
      return toast.error(
        "O subtipo selecionado não pertence ao tipo informado.",
      );
    }

    if (
      isRodentOccurrence &&
      !form.rodent_species_id
    ) {
      return toast.error(
        "Selecione a espécie do roedor para atualizar esta ocorrência.",
      );
    }

    if (
      isRodentOccurrence &&
      form.rodent_species_id &&
      !rodentSpeciesError &&
      rodentSpecies.length > 0 &&
      !selectedRodentSpecies
    ) {
      return toast.error(
        "A espécie de roedor selecionada não está mais disponível no cadastro oficial.",
      );
    }

    setBusy(true);

    try {
      const existingCustomFields =
        occurrence.custom_fields &&
        typeof occurrence.custom_fields === "object"
          ? occurrence.custom_fields
          : {};

      const custom_fields = {
        ...existingCustomFields,
      };

      if (isRodentOccurrence) {
        const officialSpecies =
          selectedRodentSpecies || {
            id: form.rodent_species_id,
            name: form.rodent_species_name || null,
          };

        custom_fields.species_id =
          form.rodent_species_id || null;
        custom_fields.rodent_species_id =
          form.rodent_species_id || null;
        custom_fields.species_name =
          officialSpecies?.name ||
          form.rodent_species_name ||
          null;
        custom_fields.rodent_species_name =
          officialSpecies?.name ||
          form.rodent_species_name ||
          null;

        if (
          officialSpecies?.scientific_name
        ) {
          custom_fields.rodent_species_scientific_name =
            officialSpecies.scientific_name;
        }

        if (form.rodent_species_id) {
          custom_fields.especie =
            officialSpecies?.name ||
            form.rodent_species_name ||
            null;
        }
      }

      const payload = {
        ...form,
        custom_fields,

        // O backend usa estes campos para reprocessar a ocorrência
        // e manter o BI sincronizado após a edição.
        rodent_species_id:
          isRodentOccurrence
            ? form.rodent_species_id || null
            : null,
        rodent_species_name:
          isRodentOccurrence
            ? (
                selectedRodentSpecies?.name ||
                form.rodent_species_name ||
                null
              )
            : null,

        category:
          normalizeCategoryName(
            form.category,
          ),

        occurrence_class:
          occurrenceClass,

        occurred_at:
          form.occurred_at
            ? `${form.occurred_at}:00`
            : null,

        type_id:
          form.type_id ||
          null,

        subtype_id:
          form.subtype_id ||
          null,

        subarea_id:
          form.subarea_id ||
          null,

        responsible_id:
          can(
            "ocorrencias",
            "assign",
          )
            ? (
                form.responsible_id ||
                ""
              )
            : null,
      };

      const { data } =
        await api.put(
          `/occurrences/${occurrence.id}`,
          payload,
        );

      toast.success(
        `Ocorrência atualizada${
          data.changes?.length
            ? ` — ${data.changes.length} campo(s) alterado(s)`
            : ""
        }`,
      );

      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(
          err.response?.data?.detail,
        ),
      );
    } finally {
      setBusy(false);
    }
  };

  const sel = (
    label,
    value,
    items,
    onVal,
    testid,
    placeholder = "Selecione",
    optional = false,
  ) => (
    <div className="space-y-1.5">
      <Label className="text-xs">
        {label}
      </Label>

      <Select
        value={
          value ||
          (optional
            ? NONE
            : "")
        }
        onValueChange={(v) =>
          onVal(
            v === NONE
              ? ""
              : v,
          )
        }
      >
        <SelectTrigger
          className="h-9"
          data-testid={testid}
        >
          <SelectValue
            placeholder={
              placeholder
            }
          />
        </SelectTrigger>

        <SelectContent>
          {optional && (
            <SelectItem
              value={NONE}
            >
              — Nenhum —
            </SelectItem>
          )}

          {items.map(
            (item) => (
              <SelectItem
                key={
                  item.id ||
                  item
                }
                value={
                  item.id ||
                  item
                }
              >
                {item.name ||
                  item}
              </SelectItem>
            ),
          )}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <Dialog
      open={open}
      onOpenChange={
        onOpenChange
      }
    >
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto scrollbar-thin"
        data-testid="occurrence-edit-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">
            Editar ocorrência{" "}
            {occurrence.protocol}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {sel(
              "Categoria *",
              form.category,
              categoryItems,
              (value) =>
                set({
                  category:
                    value,
                  type_id: "",
                  subtype_id:
                    "",
                }),
              "edit-category-select",
            )}

            {sel(
              "Tipo",
              form.type_id,
              availableTypes,
              (value) =>
                set({
                  type_id:
                    value,
                  subtype_id:
                    "",
                }),
              "edit-type-select",
              "Opcional",
              true,
            )}

            {sel(
              "Subtipo",
              form.subtype_id,
              availableSubtypes,
              (value) =>
                set({
                  subtype_id:
                    value,
                }),
              "edit-subtype-select",
              "Opcional",
              true,
            )}
          </div>

          {isRodentOccurrence && (
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                  Controle de roedores
                </p>
                <p className="text-[11px] text-muted-foreground">
                  A espécie é vinculada ao cadastro oficial para que o Dashboard
                  reconheça corretamente os indicadores históricos e futuros.
                </p>
              </div>

              {sel(
                "Espécie do roedor *",
                form.rodent_species_id,
                (() => {
                  const items = rodentSpecies
                    .filter((item) => item?.id)
                    .map((item) => ({
                      id: String(item.id),
                      name:
                        item.scientific_name
                          ? `${item.name} — ${item.scientific_name}`
                          : item.name,
                    }));

                  if (
                    form.rodent_species_id &&
                    !items.some(
                      (item) =>
                        String(item.id) ===
                        String(form.rodent_species_id),
                    )
                  ) {
                    items.unshift({
                      id: String(form.rodent_species_id),
                      name:
                        form.rodent_species_name ||
                        "Espécie registrada anteriormente",
                    });
                  }

                  return items;
                })(),
                (value) => {
                  const selected =
                    rodentSpecies.find(
                      (item) =>
                        String(item.id) ===
                        String(value),
                    );

                  set({
                    rodent_species_id: value,
                    rodent_species_name:
                      selected?.name || "",
                  });
                },
                "edit-rodent-species-select",
                rodentSpeciesLoading
                  ? "Carregando espécies..."
                  : "Selecione a espécie",
              )}

              {rodentSpeciesError && (
                <p className="text-[11px] text-amber-600">
                  O cadastro oficial de espécies não respondeu agora.
                  A espécie já registrada foi preservada para não bloquear a edição.
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {sel(
              "Prioridade *",
              form.priority,
              PRIORITIES.map(
                (priority) => ({
                  id: priority,
                  name:
                    PRIORITY_LABELS[
                      priority
                    ],
                }),
              ),
              (value) =>
                set({
                  priority:
                    value,
                }),
              "edit-priority-select",
            )}

            {sel(
              "Origem",
              form.origin,
              ORIGINS.map(
                (origin) => ({
                  id: origin,
                  name: origin,
                }),
              ),
              (value) =>
                set({
                  origin:
                    value,
                }),
              "edit-origin-select",
            )}

            {user.role !==
            "cliente" ? (
              sel(
                "Classificação",
                form.occurrence_class,
                Object.entries(
                  OCC_CLASS_LABELS,
                ).map(
                  ([
                    key,
                    value,
                  ]) => ({
                    id: key,
                    name: value,
                  }),
                ),
                (value) =>
                  set({
                    occurrence_class:
                      value,
                    category:
                      "",
                    type_id:
                      "",
                    subtype_id:
                      "",
                  }),
                "edit-class-select",
              )
            ) : (
              <div />
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">
              Descrição *
            </Label>

            <Textarea
              rows={4}
              value={
                form.description
              }
              onChange={(
                event,
              ) =>
                set({
                  description:
                    event.target
                      .value,
                })
              }
              className="resize-none"
              data-testid="edit-description-input"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">
                Data e hora da ocorrência
              </Label>

              <Input
                type="datetime-local"
                value={
                  form.occurred_at
                }
                onChange={(
                  event,
                ) =>
                  set({
                    occurred_at:
                      event.target
                        .value,
                  })
                }
                className="h-9"
                data-testid="edit-occurred-at-input"
              />
            </div>

            {can(
              "ocorrencias",
              "assign",
            ) &&
              sel(
                "Responsável",
                form.responsible_id,
                responsibles,
                (value) =>
                  set({
                    responsible_id:
                      value,
                  }),
                "edit-responsible-select",
                "Automático",
                true,
              )}
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Local
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {sel(
                "Cliente",
                form.client_id,
                tree,
                (value) =>
                  set({
                    client_id:
                      value,
                    unit_id:
                      "",
                    area_id:
                      "",
                    subarea_id:
                      "",
                  }),
                "edit-client-select",
              )}

              {sel(
                "Unidade",
                form.unit_id,
                client?.units ||
                  [],
                (value) =>
                  set({
                    unit_id:
                      value,
                    area_id:
                      "",
                    subarea_id:
                      "",
                  }),
                "edit-unit-select",
              )}

              {sel(
                "Área",
                form.area_id,
                unit?.areas ||
                  [],
                (value) =>
                  set({
                    area_id:
                      value,
                    subarea_id:
                      "",
                  }),
                "edit-area-select",
              )}

              {sel(
                "Subárea",
                form.subarea_id,
                area?.subareas ||
                  [],
                (value) =>
                  set({
                    subarea_id:
                      value,
                  }),
                "edit-subarea-select",
                "Opcional",
                true,
              )}
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            Toda alteração é registrada na auditoria com o valor
            anterior e o novo. O prazo de SLA acompanha a prioridade
            sem reiniciar a contagem.
          </p>

          <Button
            className="w-full"
            onClick={save}
            disabled={busy}
            data-testid="occurrence-edit-save-btn"
          >
            {busy
              ? "Salvando..."
              : "Salvar alterações"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}